import LoggerApi from './api/LoggerApi';
import AdobeDataLayerProcessor from './services/adobeDataLayerProcessor';
import AdobeWebSdkProcessor from './services/adobeWebSDKProcessor';
import AntiFlickerProcessor from './services/antiflickerProcessor';
import ContentPolicyProcessor from './services/contentSecurity/contentPolicyProcessor';
import CookieService from './services/cookieService';
import LogonChallengeProcessor from './services/logonChallengeProcessor';
import ConfigRetriever from './utils/configRetriever';
import DebugLog from './utils/debugLog';
import RteValidator from './utils/rteValidator';
import { validateC3Config } from './utils/validators';

export interface AppConfig {
  csp?: any;
  debug?: boolean;
}

export type CccConfig = {
  site: string;
  rte: string;
  app: string;
  concerns: string;
  appConfig: AppConfig;
};

export namespace ProcessableRtes {
  export type NonprodProcessableRte = 'NONPROD';
  export type ProdProcessableRte = 'PROD';
  export const NonprodProcessableRte: NonprodProcessableRte = 'NONPROD';
  export const ProdProcessableRte: ProdProcessableRte = 'PROD';
}

export interface ConcernsProcessorOptions {
  appConfig: AppConfig;
  rte: ProcessableRtes.NonprodProcessableRte | ProcessableRtes.ProdProcessableRte;
}

const ALL_CONCERNS_PROCESSORS = [
  new AdobeDataLayerProcessor(),
  new AntiFlickerProcessor(),
  new AdobeWebSdkProcessor(),
  new LogonChallengeProcessor(),
];

export default class C3ServerlessBundle {
  public async loadC3Bundle(inputConfig: CccConfig = { app: '', appConfig: { csp: {} }, concerns: '', rte: '', site: '' }) {
    LoggerApi.makePostCall(inputConfig.rte, { level: 'log', message: `LITE-${JSON.stringify(inputConfig)}` });
    const validatedInputConfig = validateC3Config(inputConfig);
    if (!validatedInputConfig) {
      return;
    }

    const { rte, site, app, concerns, appConfig } = validatedInputConfig;
    const isValidRte = RteValidator.validateRte(rte);
    if (!isValidRte) {
      return;
    }

    const cspProcessor = new ContentPolicyProcessor();
    const { csp } = appConfig as { csp };

    cspProcessor.validateClientCspValues(app, RteValidator.determineRteByBundleScript(), csp);
    cspProcessor.process(site, csp);

    const config = await ConfigRetriever.getConfig(rte, site);
    const adobeWebSdkProcessor = new AdobeWebSdkProcessor();

    const options: ConcernsProcessorOptions = { appConfig, rte } as ConcernsProcessorOptions;

    this.processConcerns(concerns, config, options);

    CookieService.callCookieDrop(adobeWebSdkProcessor.getConcernCode(), rte);
  }

  public processConcerns(concerns: string, config: any, options: ConcernsProcessorOptions) {
    const START_PROCESS_TIME = Date.now();
    DebugLog.consolelog(options.appConfig.debug, `C3 Bundle Start Processing Time = ${START_PROCESS_TIME}`);

    const updatedConcerns = this.addDefaultConcerns(concerns, config);

    const processConcerns = updatedConcerns
      .split(',')
      .map((concern) => ALL_CONCERNS_PROCESSORS.find((processor) => processor.getConcernCode() === concern.toLowerCase()))
      .filter((p) => Boolean(p));
    processConcerns.forEach((processor) => {
      DebugLog.consolelog(options.appConfig.debug, `C3 Bundle Start Processing Concern = ${processor.getConcernCode()}`);
      processor.process(config, options);
      DebugLog.consolelog(
        options.appConfig.debug,
        `C3 Bundle End Processing Concern = ${processor.getConcernCode()} Duration= ${Date.now() - START_PROCESS_TIME}`,
      );
    });
    DebugLog.consolelog(options.appConfig.debug, `C3 Bundle END Processing Time = ${Date.now()}`);
  }

  addDefaultConcerns(concerns: string, config: any) {
    const defaultConcerns = config.cccFeatureToggle.defaultConcerns;
    return defaultConcerns.reduce((acc, concern) => acc.concat(`,${concern}`), concerns);
  }
}
